import { supabase } from "@/lib/supabase";
import useAuthStore from "@/store/authStore";
import { useThreadStore } from "@/store/threadStore";

/**
 * Refreshes the threads list and removes untitled/default chats
 * @param {Object} options - Configuration options
 * @param {boolean} options.removeUntitledChats - Whether to remove untitled chats (default: true)
 * @param {string} options.userId - User ID to filter threads (optional, will use current user if not provided)
 * @param {Function} options.onProgress - Progress callback function (optional)
 * @returns {Promise<Object>} - Result object with counts and any errors
 */
export const refreshThreadsAndCleanup = async (options = {}) => {
  const {
    removeUntitledChats = true,
    userId = null,
    onProgress = null,
  } = options;

  try {
    const currentUserId = userId || useAuthStore.getState().user?.id;
    if (!currentUserId) {
      throw new Error("No user ID available for thread refresh");
    }

    const result = {
      refreshed: 0,
      removed: 0,
      errors: [],
    };

    if (onProgress) {
      onProgress({ stage: "fetching", message: "Fetching current threads..." });
    }

    // Get current threads from the store
    const { threads, setThreads } = useThreadStore.getState();

    // Fetch fresh thread data from database
    const { data: freshThreads, error: fetchError } = await supabase
      .from("threads")
      .select("*")
      .eq("user_id", currentUserId)
      .order("updated_at", { ascending: false });

    if (fetchError) {
      throw new Error(`Failed to fetch threads: ${fetchError.message}`);
    }

    if (onProgress) {
      onProgress({
        stage: "processing",
        message: `Processing ${freshThreads?.length || 0} threads...`,
      });
    }

    let threadsToKeep = freshThreads || [];
    let removedThreadIds = [];

    // Remove untitled/default chats if requested
    if (removeUntitledChats && threadsToKeep.length > 0) {
      const threadsToRemove = threadsToKeep.filter((thread) =>
        isDefaultTitle(thread.title)
      );

      if (threadsToRemove.length > 0) {
        if (onProgress) {
          onProgress({
            stage: "cleaning",
            message: `Removing ${threadsToRemove.length} untitled chats...`,
          });
        }

        // Delete untitled threads from database
        for (const thread of threadsToRemove) {
          try {
            // Delete thread messages first
            const { error: messagesError } = await supabase
              .from("thread_messages")
              .delete()
              .eq("thread_id", thread.id);

            if (messagesError) {
              console.warn(
                `Failed to delete messages for thread ${thread.id}:`,
                messagesError
              );
            }

            // Delete the thread
            const { error: threadError } = await supabase
              .from("threads")
              .delete()
              .eq("id", thread.id);

            if (threadError) {
              console.warn(
                `Failed to delete thread ${thread.id}:`,
                threadError
              );
              result.errors.push(
                `Failed to delete thread ${thread.id}: ${threadError.message}`
              );
            } else {
              removedThreadIds.push(thread.id);
              result.removed++;
            }
          } catch (error) {
            console.warn(`Error deleting thread ${thread.id}:`, error);
            result.errors.push(
              `Error deleting thread ${thread.id}: ${error.message}`
            );
          }
        }

        // Filter out removed threads
        threadsToKeep = threadsToKeep.filter(
          (thread) => !removedThreadIds.includes(thread.id)
        );
      }
    }

    // Update the thread store with fresh data
    setThreads(threadsToKeep);
    result.refreshed = threadsToKeep.length;

    // Also update workspaces to remove deleted thread references
    const { workspaces, setWorkspaces } = useAuthStore.getState();
    if (removedThreadIds.length > 0 && workspaces?.length > 0) {
      const updatedWorkspaces = workspaces.map((workspace) => {
        if (
          workspace.workspace_threads &&
          workspace.workspace_threads.length > 0
        ) {
          const filteredThreads = workspace.workspace_threads.filter(
            (threadId) => !removedThreadIds.includes(threadId)
          );

          if (filteredThreads.length !== workspace.workspace_threads.length) {
            return {
              ...workspace,
              workspace_threads: filteredThreads,
            };
          }
        }
        return workspace;
      });

      setWorkspaces(updatedWorkspaces);
    }

    if (onProgress) {
      onProgress({
        stage: "complete",
        message: `Refresh complete. ${result.refreshed} threads kept, ${result.removed} removed.`,
      });
    }

    return result;
  } catch (error) {
    console.error("Error in refreshThreadsAndCleanup:", error);
    return {
      refreshed: 0,
      removed: 0,
      errors: [error.message],
    };
  }
};

/**
 * Checks if a thread title is considered a default/untitled title
 * @param {string} title - The thread title to check
 * @returns {boolean} - True if the title is considered default/untitled
 */
export const isDefaultTitle = (title) => {
  if (!title) return true;

  const defaultTitles = [
    "New Chat",
    "Untitled Chat",
    "New Thread",
    "Untitled Thread",
  ];

  // Check exact matches
  if (defaultTitles.includes(title)) {
    return true;
  }

  // Check patterns like "Chat abc123..."
  if (title.startsWith("Chat ") && title.match(/^Chat [a-f0-9]{6}\.\.\.$/i)) {
    return true;
  }

  return false;
};

/**
 * Removes a specific thread and refreshes the UI
 * @param {string} threadId - The thread ID to remove
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - Success status
 */
export const removeThreadAndRefresh = async (threadId, options = {}) => {
  try {
    const {
      updateWorkspaces = true,
      navigateIfCurrent = true,
      onSuccess = null,
    } = options;

    const {
      currentThreadId,
      threads,
      setThreads,
      setCurrentThread,
      setCurrentThreadId,
      setCurrentThreadTitle,
      setMessages,
    } = useThreadStore.getState();

    const isCurrentThread = currentThreadId === threadId;

    // Remove from store optimistically
    const updatedThreads = threads.filter((t) => t.id !== threadId);
    setThreads(updatedThreads);

    // Clear current thread if it's the one being deleted
    if (isCurrentThread) {
      setCurrentThread(null);
      setCurrentThreadId(null);
      setCurrentThreadTitle(null);
      setMessages([]);

      if (navigateIfCurrent && window.location.pathname.includes(threadId)) {
        const { navigate } = await import("react-router-dom");
        navigate("/chat/new");
      }
    }

    // Update workspaces if requested
    if (updateWorkspaces) {
      const { workspaces, setWorkspaces } = useAuthStore.getState();
      if (workspaces?.length > 0) {
        const updatedWorkspaces = workspaces.map((workspace) => {
          if (workspace.workspace_threads?.includes(threadId)) {
            return {
              ...workspace,
              workspace_threads: workspace.workspace_threads.filter(
                (id) => id !== threadId
              ),
            };
          }
          return workspace;
        });
        setWorkspaces(updatedWorkspaces);
      }
    }

    if (onSuccess) {
      onSuccess();
    }

    return true;
  } catch (error) {
    console.error("Error in removeThreadAndRefresh:", error);
    return false;
  }
};

/**
 * Bulk cleanup of untitled threads for a specific user
 * @param {string} userId - User ID (optional, uses current user if not provided)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<Object>} - Cleanup result
 */
export const bulkCleanupUntitledThreads = async (
  userId = null,
  onProgress = null
) => {
  try {
    const currentUserId = userId || useAuthStore.getState().user?.id;
    if (!currentUserId) {
      throw new Error("No user ID available for cleanup");
    }

    if (onProgress) {
      onProgress({
        stage: "scanning",
        message: "Scanning for untitled threads...",
      });
    }

    // Find all untitled threads for the user
    const { data: untitledThreads, error: fetchError } = await supabase
      .from("threads")
      .select("id, title")
      .eq("user_id", currentUserId);

    if (fetchError) {
      throw new Error(`Failed to fetch threads: ${fetchError.message}`);
    }

    const threadsToDelete = untitledThreads.filter((thread) =>
      isDefaultTitle(thread.title)
    );

    if (threadsToDelete.length === 0) {
      return { removed: 0, errors: [] };
    }

    if (onProgress) {
      onProgress({
        stage: "deleting",
        message: `Deleting ${threadsToDelete.length} untitled threads...`,
      });
    }

    const result = { removed: 0, errors: [] };

    // Delete threads in batches
    const batchSize = 10;
    for (let i = 0; i < threadsToDelete.length; i += batchSize) {
      const batch = threadsToDelete.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async (thread) => {
          try {
            // Delete messages first
            await supabase
              .from("thread_messages")
              .delete()
              .eq("thread_id", thread.id);

            // Delete thread
            const { error } = await supabase
              .from("threads")
              .delete()
              .eq("id", thread.id);

            if (error) {
              result.errors.push(
                `Failed to delete thread ${thread.id}: ${error.message}`
              );
            } else {
              result.removed++;
            }
          } catch (error) {
            result.errors.push(
              `Error deleting thread ${thread.id}: ${error.message}`
            );
          }
        })
      );

      if (onProgress) {
        onProgress({
          stage: "deleting",
          message: `Deleted ${Math.min(i + batchSize, threadsToDelete.length)} of ${threadsToDelete.length} threads...`,
        });
      }
    }

    // Refresh the threads list
    await refreshThreadsAndCleanup({ removeUntitledChats: false });

    return result;
  } catch (error) {
    console.error("Error in bulkCleanupUntitledThreads:", error);
    return { removed: 0, errors: [error.message] };
  }
};

/**
 * Cleans up stale thread references from a specific workspace
 * @param {string} workspaceId - The workspace ID to clean up
 * @param {Array} threadIds - Array of thread IDs that should exist
 * @returns {Promise<Object>} - Cleanup result
 */
export const cleanupStaleWorkspaceThreads = async (
  workspaceId,
  threadIds = []
) => {
  try {
    if (!workspaceId) {
      throw new Error("Workspace ID is required");
    }

    // Get the workspace
    const { data: workspace, error: fetchError } = await supabase
      .from("workspaces")
      .select("workspace_threads")
      .eq("id", workspaceId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch workspace: ${fetchError.message}`);
    }

    if (!workspace?.workspace_threads?.length) {
      return { removed: 0, errors: [] };
    }

    // If no threadIds provided, check which threads actually exist in database
    let existingThreadIds = threadIds;
    if (threadIds.length === 0) {
      const { data: existingThreads, error: threadsError } = await supabase
        .from("threads")
        .select("id")
        .in("id", workspace.workspace_threads);

      if (threadsError) {
        throw new Error(
          `Failed to check existing threads: ${threadsError.message}`
        );
      }

      existingThreadIds = existingThreads.map((t) => t.id);
    }

    // Find stale thread IDs
    const staleThreadIds = workspace.workspace_threads.filter(
      (threadId) => !existingThreadIds.includes(threadId)
    );

    if (staleThreadIds.length === 0) {
      return { removed: 0, errors: [] };
    }

    // Remove stale thread IDs from workspace
    const updatedWorkspaceThreads = workspace.workspace_threads.filter(
      (threadId) => !staleThreadIds.includes(threadId)
    );

    // Update the workspace in the database
    const { error: updateError } = await supabase
      .from("workspaces")
      .update({
        workspace_threads: updatedWorkspaceThreads,
        updated_at: new Date().toISOString(),
      })
      .eq("id", workspaceId);

    if (updateError) {
      throw new Error(`Failed to update workspace: ${updateError.message}`);
    }

    // Update the local workspace state
    const { workspaces, setWorkspaces } = useAuthStore.getState();
    const updatedWorkspaces = workspaces.map((ws) => {
      if (ws.id === workspaceId) {
        return {
          ...ws,
          workspace_threads: updatedWorkspaceThreads,
        };
      }
      return ws;
    });
    setWorkspaces(updatedWorkspaces);

    return {
      removed: staleThreadIds.length,
      errors: [],
      staleThreadIds,
    };
  } catch (error) {
    console.error("Error cleaning up stale workspace threads:", error);
    return {
      removed: 0,
      errors: [error.message],
    };
  }
};

/**
 * Bulk cleanup of stale thread references across all workspaces for a user
 * @param {string} userId - User ID (optional, uses current user if not provided)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<Object>} - Cleanup result
 */
export const bulkCleanupStaleWorkspaceThreads = async (
  userId = null,
  onProgress = null
) => {
  try {
    const currentUserId = userId || useAuthStore.getState().user?.id;
    if (!currentUserId) {
      throw new Error("No user ID available for cleanup");
    }

    if (onProgress) {
      onProgress({
        stage: "scanning",
        message: "Scanning workspaces for stale thread references...",
      });
    }

    // Get all workspaces for the user
    const { workspaces } = useAuthStore.getState();
    if (!workspaces?.length) {
      return { removed: 0, errors: [] };
    }

    const result = { removed: 0, errors: [] };

    // Process each workspace
    for (const workspace of workspaces) {
      if (workspace.workspace_threads?.length > 0) {
        if (onProgress) {
          onProgress({
            stage: "cleaning",
            message: `Cleaning workspace: ${workspace.name}...`,
          });
        }

        const cleanupResult = await cleanupStaleWorkspaceThreads(workspace.id);
        result.removed += cleanupResult.removed;
        result.errors.push(...cleanupResult.errors);
      }
    }

    if (onProgress) {
      onProgress({
        stage: "complete",
        message: `Cleanup complete. Removed ${result.removed} stale thread references.`,
      });
    }

    return result;
  } catch (error) {
    console.error("Error in bulk cleanup of stale workspace threads:", error);
    return { removed: 0, errors: [error.message] };
  }
};
